import { useEffect, useRef } from 'react';
import { useMapController } from '../map/MapShell.js';
import type { Waypoint } from '../../lib/db.js';

interface WaypointEditorProps {
  waypoints: Waypoint[];
  onAdd: (lat: number, lon: number) => void;
  onMove: (index: number, lat: number, lon: number) => void;
  onSelect: (index: number) => void;
  /** Set (a fresh object each time) to pan the map to a point — used by the
   *  waypoint list editor's tap-to-pan row (spec 14B). */
  panTo?: { lat: number; lon: number } | null;
}

/** Imperative bridge: map taps append waypoints, markers drag/select (spec 05). */
export function WaypointEditor({
  waypoints,
  onAdd,
  onMove,
  onSelect,
  panTo,
}: WaypointEditorProps): null {
  const controller = useMapController();
  // Keep the latest callbacks without re-subscribing map handlers each render.
  const handlers = useRef({ onAdd, onMove, onSelect });
  handlers.current = { onAdd, onMove, onSelect };

  useEffect(() => {
    if (!controller) return;
    controller.setWaypointHandlers({
      onDragEnd: (i, lat, lon) => handlers.current.onMove(i, lat, lon),
      onMarkerClick: (i) => handlers.current.onSelect(i),
    });
    const unsub = controller.onMapClick((lat, lon) => handlers.current.onAdd(lat, lon));
    return () => {
      unsub();
      controller.clearWaypoints();
    };
  }, [controller]);

  useEffect(() => {
    controller?.renderWaypoints(waypoints);
  }, [controller, waypoints]);

  useEffect(() => {
    if (!controller || !panTo) return;
    controller.map.panTo([panTo.lat, panTo.lon]);
  }, [controller, panTo]);

  return null;
}
